import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, TrendingUp, ChevronRight } from 'lucide-react';
import { edificiosApi, reportesApi, cotizacionesApi } from '../../lib/api';
import { formatCurrency, formatPeriodo, formatNumber, cn } from '../../lib/utils';
import { CurrencyToggle } from '../../components/common/CurrencyToggle';
import { PeriodSelector } from '../../components/common/PeriodSelector';
import { LoadingState, EmptyState, ErrorState } from '../../components/common/EmptyState';
import { Button } from '../../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { toast } from 'sonner';

export const AdminReporteEdificios = () => {
  const navigate = useNavigate();
  const [edificios, setEdificios] = useState([]);
  const [selectedEdificio, setSelectedEdificio] = useState('');
  const [reporte, setReporte] = useState(null);
  const [cotizaciones, setCotizaciones] = useState({});
  const [displayMoneda, setDisplayMoneda] = useState('ARS');
  const [loading, setLoading] = useState(true);
  const [loadingReporte, setLoadingReporte] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchInitialData();
  }, []);

  useEffect(() => {
    if (selectedEdificio) {
      fetchReporte();
    }
  }, [selectedEdificio]);

  const fetchInitialData = async () => {
    try {
      setLoading(true);
      const [edificiosRes, cotizacionesRes] = await Promise.all([
        edificiosApi.list(),
        cotizacionesApi.list(),
      ]);
      setEdificios(edificiosRes.data);
      
      const cotizacionesMap = {};
      cotizacionesRes.data.forEach(c => {
        cotizacionesMap[c.periodo] = c.usd_ars;
      });
      setCotizaciones(cotizacionesMap);
      
      if (edificiosRes.data.length > 0) {
        setSelectedEdificio(edificiosRes.data[0].edificio_id);
      }
    } catch (err) {
      console.error('Error fetching data:', err);
      setError('Error al cargar los datos');
    } finally {
      setLoading(false);
    }
  };

  const fetchReporte = async () => {
    if (!selectedEdificio) return;
    
    try {
      setLoadingReporte(true);
      const response = await reportesApi.edificio(selectedEdificio);
      setReporte(response.data);
    } catch (err) {
      console.error('Error fetching reporte:', err);
      toast.error('Error al cargar el reporte');
    } finally {
      setLoadingReporte(false);
    }
  };

  const getConvertedValue = (monto, moneda, periodo) => {
    if (moneda === displayMoneda) return monto;
    
    const cotizacion = cotizaciones[periodo];
    if (!cotizacion) return monto;
    
    if (moneda === 'ARS' && displayMoneda === 'USD') {
      return monto / cotizacion;
    } else if (moneda === 'USD' && displayMoneda === 'ARS') {
      return monto * cotizacion;
    }
    
    return monto;
  };

  if (loading) return <LoadingState message="Cargando datos..." />;
  if (error) return <ErrorState message={error} onRetry={fetchInitialData} />;

  return (
    <div className="p-4 md:p-8">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900">Reporte por Edificio</h1>
          <p className="text-slate-500 mt-1">Evolución mensual agregada por edificio</p>
        </div>
        
        <CurrencyToggle value={displayMoneda} onChange={setDisplayMoneda} />
      </div>

      {/* Edificio selector */}
      <div className="mb-6">
        <Select value={selectedEdificio} onValueChange={setSelectedEdificio}>
          <SelectTrigger className="w-full md:w-[300px]" data-testid="select-edificio">
            <SelectValue placeholder="Seleccionar edificio" />
          </SelectTrigger>
          <SelectContent>
            {edificios.map((e) => (
              <SelectItem key={e.edificio_id} value={e.edificio_id}>
                {e.nombre}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {loadingReporte ? (
        <LoadingState message="Cargando reporte..." />
      ) : reporte ? (
        <div className="space-y-6">
          {/* Summary cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-white border border-slate-200 rounded-lg p-4 md:p-6">
              <div className="flex items-center gap-3 mb-2">
                <Building2 className="w-5 h-5 text-slate-400" />
                <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                  Edificio
                </span>
              </div>
              <p className="text-lg font-bold text-slate-900">{reporte.edificio?.nombre}</p>
              <p className="text-sm text-slate-500">{reporte.edificio?.direccion}</p>
            </div>
            
            <div className="bg-white border border-slate-200 rounded-lg p-4 md:p-6 text-center">
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">
                Total Unidades
              </p>
              <p className="text-3xl font-bold text-slate-900">{reporte.total_unidades}</p>
            </div>
            
            <div className="bg-white border border-slate-200 rounded-lg p-4 md:p-6 text-center">
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">
                Alquiladas
              </p>
              <p className="text-3xl font-bold text-green-700">{reporte.unidades_alquiladas}</p>
            </div>
            
            <div className="bg-white border border-slate-200 rounded-lg p-4 md:p-6 text-center">
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">
                Vacantes
              </p>
              <p className="text-3xl font-bold text-amber-600">{reporte.unidades_vacantes}</p>
            </div>
          </div>

          {/* Evolution table */}
          {reporte.evolucion_mensual?.length > 0 ? (
            <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
              <div className="p-4 md:p-6 border-b border-slate-200">
                <h2 className="text-lg font-semibold text-slate-900">Evolución Mensual</h2>
              </div>
              
              <div className="overflow-x-auto">
                <table className="w-full min-w-[700px]">
                  <thead>
                    <tr className="bg-slate-50 border-b border-slate-200">
                      <th className="text-left text-xs font-semibold uppercase tracking-wider text-slate-500 py-3 px-4">
                        Período
                      </th>
                      <th className="text-right text-xs font-semibold uppercase tracking-wider text-slate-500 py-3 px-4">
                        Unidades
                      </th>
                      <th className="text-right text-xs font-semibold uppercase tracking-wider text-slate-500 py-3 px-4">
                        Ingreso Total
                      </th>
                      <th className="text-right text-xs font-semibold uppercase tracking-wider text-slate-500 py-3 px-4">
                        Egreso Total
                      </th>
                      <th className="text-right text-xs font-semibold uppercase tracking-wider text-slate-500 py-3 px-4">
                        Fee Total
                      </th>
                      <th className="text-right text-xs font-semibold uppercase tracking-wider text-slate-500 py-3 px-4">
                        Ganancia Total
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {reporte.evolucion_mensual.map((row) => {
                      const cotizacion = cotizaciones[row.periodo];
                      
                      return (
                        <tr 
                          key={row.periodo}
                          className="border-b border-slate-100 hover:bg-slate-50"
                          data-testid={`row-${row.periodo}`}
                        >
                          <td className="py-4 px-4 font-medium text-slate-900">
                            {formatPeriodo(row.periodo)}
                          </td>
                          <td className="py-4 px-4 text-right text-slate-700">
                            {row.unidades_count}
                          </td>
                          <td className="py-4 px-4 text-right font-mono text-slate-700">
                            {formatCurrency(
                              getConvertedValue(row.total_ingreso, row.moneda, row.periodo),
                              displayMoneda
                            )}
                          </td>
                          <td className="py-4 px-4 text-right font-mono text-red-700">
                            {formatCurrency(
                              getConvertedValue(row.total_egreso, row.moneda, row.periodo),
                              displayMoneda
                            )}
                          </td>
                          <td className="py-4 px-4 text-right font-mono text-slate-700">
                            {formatCurrency(
                              getConvertedValue(row.total_fee, row.moneda, row.periodo),
                              displayMoneda
                            )}
                          </td>
                          <td className={cn(
                            "py-4 px-4 text-right font-mono font-semibold",
                            row.total_ganancia >= 0 ? "text-green-700" : "text-red-700"
                          )}>
                            {formatCurrency(
                              getConvertedValue(row.total_ganancia, row.moneda, row.periodo),
                              displayMoneda
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <EmptyState
              icon={TrendingUp}
              title="Sin datos de evolución"
              description="Aún no hay resultados publicados para este edificio."
            />
          )}
        </div>
      ) : (
        <EmptyState
          icon={Building2}
          title="Selecciona un edificio"
          description="Elige un edificio para ver su reporte de evolución mensual."
        />
      )}
    </div>
  );
};

export default AdminReporteEdificios;
